<template>
  <div class="power-grid-wrapper">
    <div v-if="IS_DEV_MODE" class="dev-banner">
      ⚙ MODO DESARROLLO (Puntos deshabilitados)
    </div>

    <div
      v-if="showSplash"
      id="splash-screen"
      :class="{ 'fade-out': startFading }"
    >
      <img
        src="/minigames/market/pantalla_inicial.png"
        alt="Fondo"
        class="splash-bg-img"
      />

      <div class="splash-overlay"></div>

      <div class="splash-content">
        <h1 class="game-title">Electric Mad Market</h1>

        <p class="game-desc">
          Ajusta las ofertas a tu previsión <br>
          antes de que cierren los mercados. <br><br>
          ¡Ten cuidado! Tu previsión puede cambiar <br>
          y tus activos se pueden averiar.<br><br>
          ¡No te desvíes y coloca bien tus unidades de oferta!
        </p>

        <button class="btn-start" @click="startGame">
          INICIAR
        </button>
      </div>
    </div>

    <header id="header">
      <div id="phase-txt">{{ phaseName }}</div>
      <div id="timer">⏱ {{ timeLeft }}s</div>
      <div id="score">Cobertura: {{ currentScore }}%</div>
    </header>

    <div class="game-body">
      <main id="game-container" ref="gameContainer">
        <transition name="pop">
          <div v-if="msg" id="msg">
            {{ msg }}
          </div>
        </transition>

        <div v-if="gameOver" id="overlay">
          <h2>MERCADO CERRADO</h2>

          <div id="res-score">
            Puntos: {{ finalScore }}
          </div>

          <div id="res-detail" v-html="resDetail"></div>

          <p v-if="IS_DEV_MODE" class="dev-txt">
            Modo Test: Puntuación local (sin envío al servidor)
          </p>

          <p v-if="verifiedScore !== null" class="verified-txt">
            Verificado: {{ verifiedScore }} puntos
          </p>

          <p v-if="verifiedMoney !== null" class="verified-txt">
            Dinero ganado:
            {{ Number(verifiedMoney).toLocaleString("es-ES") }} €
          </p>

          <button class="btn btn-reset" @click="resetGame">
            NUEVO CICLO
          </button>
        </div>
      </main>

      <aside class="sidebar-right">
        <div class="sidebar-section-blocks">
          <span class="sidebar-title">
            UNIDADES DE OFERTA
          </span>

          <button
            v-for="n in 3"
            :key="n"
            class="btn btn-size add-btn"
            :class="{
              disabled: !canSelectBlock,
              selected: selectedSize === n
            }"
            :disabled="!canSelectBlock"
            @click="selectBlock(n)"
          >
            <span>{{ n }}x{{ n }}</span>
            <small>{{ n * n }}MW</small>
          </button>
        </div>

        <div class="character-sprite-container">
          <span class="sidebar-title">
            AGENTE
          </span>

          <div class="sprite-viewport" :class="characterState">
            <div class="sprite-avatar-placeholder">
              <img
                :src="characterImageSrc"
                alt="Avatar Operador"
                class="avatar-img"
              />
            </div>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import * as Phaser from "phaser";
import axios from "@/axios";
import CryptoJS from "crypto-js";
import { useGameStore } from "@/stores/game";
import Swal from "sweetalert2";

const IS_DEV_MODE = false;

const MARKET_PHASES = {
  1: { name: "Mercado DIARIO", time: 25, type: "fill" },
  2: { name: "Mercado INTRADIARIO", time: 25, type: "fill" },
};

const BLOCK_COLORS = {
  1: 0xfbbf24,
  2: 0x818cf8,
  3: 0xec4899,
};

function createCustomCursor(symbol) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
    <circle cx="16" cy="16" r="14" fill="#020617" stroke="#38bdf8" stroke-width="2"/>
    <text x="16" y="21" font-size="18" font-weight="bold" fill="#ffffff" text-anchor="middle" font-family="sans-serif">${symbol}</text>
  </svg>`;

  return `url('data:image/svg+xml;utf8,${encodeURIComponent(svg)}') 16 16, pointer`;
}

const CURSOR_ADD = createCustomCursor("+");
const CURSOR_REMOVE = createCustomCursor("-");

async function startMarketPartida() {
  if (IS_DEV_MODE) {
    return {
      id_partida: "DEV_SESSION",
      clave_temporal: "DEV_KEY"
    };
  }

  try {
    const response = await axios.post(
      "/api/minigames/partida/iniciar",
      {
        game_id: "market"
      }
    );

    if (response.data?.status === "success") {
      return response.data.data;
    }

    throw new Error(
      response.data?.message ||
      response.data?.data?.message ||
      "Error al iniciar partida"
    );

  } catch (error) {
    const msg =
      error?.response?.data?.message ||
      error?.response?.data?.data?.message ||
      error?.message ||
      "No se pudo iniciar la partida.";

    throw new Error(msg);
  }
}

async function finalizeMarketPartida(idPartida, stats, secretKey) {
  if (IS_DEV_MODE) {
    console.log(
      "🛠️ [DEV MODE] Partida finalizada. Stats enviadas (Simulación):",
      stats
    );

    return {
      status: "success",
      data: {
        score: stats.final_score,
        dinero: 0,
      },
    };
  }

  const statsOrdenados = Object.keys(stats)
    .sort()
    .reduce((obj, key) => {
      obj[key] = stats[key];
      return obj;
    }, {});

  const statsStr = JSON.stringify(statsOrdenados);

  const hash = CryptoJS.HmacSHA256(
    statsStr,
    secretKey
  ).toString(CryptoJS.enc.Hex);

  const response = await axios.post(
    "/api/minigames/partida/finalizar",
    {
      id_partida: idPartida,
      stats,
      hash,
    }
  );

  return response.data;
}

export default {
  name: "PowerGridGame",

  data() {
    return {
      IS_DEV_MODE,
      store: useGameStore(),
      idPartida: null,
      claveTemporal: null,
      verifiedScore: null,
      verifiedMoney: null,
      timerInterval: null,
      breakdownInterval: null,
      game: null,
      scene: null,
      lastCurveUpdate: 0,
      showSplash: true,
      startFading: false,
      gameOver: false,
      msg: "",
      phaseName: "ESPERANDO...",
      timeLeft: 45,
      currentScore: 0,
      finalScore: 0,
      resDetail: "",
      selectedSize: null,
      isFalling: false,
      hoveredBlock: null,
      characterState: "idle",
      characterTimer: null,
      statsInternas: this.createInitialStats(),

      gameState: {
        active: false,
        currentPhase: 1,
        unit: 0,
        cols: 12,
        rows: 20,
        blocks: [],
        currentBlock: null,
        baseCurveColumns: [],
        curveColumns: [],
        reeTarget: 0,
      },
    };
  },

  computed: {
    canSelectBlock() {
      const currentPhase =
        MARKET_PHASES[this.gameState.currentPhase];

      return (
        this.gameState.active &&
        currentPhase?.type !== "ree" &&
        !this.isFalling
      );
    },

    groundY() {
      const u = this.gameState.unit;
      const r = this.gameState.rows;

      return (r - 1) * u;
    },

    characterEmoticon() {
      switch (this.characterState) {
        case "phase-1":
          return "📈";
        case "phase-2":
          return "⚡";
        case "block-placed":
          return "👍";
        case "block-destroyed":
          return "⚠️";
        case "breakdown-warning":
          return "🚨";
        default:
          return "🤖";
      }
    },

    characterImageSrc() {
      switch (this.characterState) {
        case "phase-1":
        case "phase-2":
        case "block-placed":
          return require(
            "@/assets/minigames/madmarket/simulito-alegre.png"
          );

        case "block-destroyed":
          return require(
            "@/assets/minigames/madmarket/simulito-serio.png"
          );

        case "breakdown-warning":
          return require(
            "@/assets/minigames/madmarket/simulito-sorpresa.png"
          );

        default:
          return require(
            "@/assets/minigames/madmarket/simulito-alegre.png"
          );
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.initPhaser();
      window.addEventListener("keydown", this.handleKeyboard);
    });
  },

  beforeDestroy() {
    this.cleanupGame();
  },

  methods: {
    triggerCharacterReaction(stateType) {
      this.characterState = stateType;

      if (this.characterTimer) {
        clearTimeout(this.characterTimer);
      }

      if (
        [
          "block-placed",
          "block-destroyed",
          "breakdown-warning",
        ].includes(stateType)
      ) {
        this.characterTimer = setTimeout(() => {
          this.characterState =
            `phase-${this.gameState.currentPhase}`;
        }, 1500);
      }
    },

    createInitialStats() {
      return {
        startTime: null,
        totalTimeMs: 0,
        bloques_colocados: 0,
        bloques_eliminados: 0,
        fases_completadas: 0,
      };
    },

    cleanupGame() {
      window.removeEventListener("keydown", this.handleKeyboard);

      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }

      if (this.breakdownInterval) {
        clearInterval(this.breakdownInterval);
        this.breakdownInterval = null;
      }

      if (this.characterTimer) {
        clearTimeout(this.characterTimer);
        this.characterTimer = null;
      }

      if (this.game) {
        this.game.destroy(true);
        this.game = null;
      }

      this.scene = null;
    },

    initPhaser() {
      const self = this;
      const container = this.$refs.gameContainer;

      if (!container) return;

      const config = {
        type: Phaser.AUTO,
        parent: container,
        width: container.clientWidth,
        height: container.clientHeight,
        transparent: true,

        scale: {
          mode: Phaser.Scale.FIT,
          autoCenter: Phaser.Scale.CENTER_BOTH,
        },

        scene: {
          create() {
            self.scene = this;

            this.gridGraphics = this.add.graphics();
            this.demandGraphics = this.add.graphics();

            this.demandGraphics.setDepth(100);

            this.input.on("pointerdown", (pointer) => {
              self.handleGlobalClick(pointer);
            });

            this.input.on("pointermove", (pointer) => {
              self.handlePointerMove(pointer);
            });

            self.drawGrid();
          },

          update(time) {
            if (!self.gameState.active) return;

            self.updatePhysics();

            const phase =
              MARKET_PHASES[self.gameState.currentPhase];

            const isIntradiario =
              phase?.name.includes("INTRA");

            if (
              isIntradiario &&
              time - self.lastCurveUpdate >= 100
            ) {
              self.lastCurveUpdate = time;

              self.updateCurveFluctuations(time);
              self.drawCurve();
              self.calculateScore();
            }
          },
        },
      };

      this.game = new Phaser.Game(config);

      this.gameState.unit =
        config.width / this.gameState.cols;

      this.gameState.rows = Math.floor(
        config.height / this.gameState.unit
      );
    },

    handlePointerMove(pointer) {
      if (!this.gameState.active || this.isFalling) return;

      const state = this.gameState;
      const currentPhaseData =
        MARKET_PHASES[state.currentPhase];

      const isIntradiario =
        currentPhaseData?.name.includes("INTRA");

      const isRestricciones =
        currentPhaseData?.type === "ree";

      let foundBlock = null;

      if (
        this.selectedSize &&
        !state.currentBlock &&
        currentPhaseData?.type !== "ree"
      ) {
        if (this.hoveredBlock && this.hoveredBlock.active) {
          this.hoveredBlock.setStrokeStyle(
            2,
            0x000000,
            0.4
          );

          this.hoveredBlock = null;
        }

        const container = this.$refs.gameContainer;

        if (container) {
          container.style.cursor = CURSOR_ADD;
        }

        return;
      }

      if (isIntradiario || isRestricciones) {
        for (
          let i = state.blocks.length - 1;
          i >= 0;
          i--
        ) {
          const b = state.blocks[i];

          const rect =
            new Phaser.Geom.Rectangle(
              b.x,
              b.y,
              b.width,
              b.height
            );

          if (
            Phaser.Geom.Rectangle.Contains(
              rect,
              pointer.worldX,
              pointer.worldY
            )
          ) {
            foundBlock = b;
            break;
          }
        }
      }

      if (this.hoveredBlock !== foundBlock) {
        if (
          this.hoveredBlock &&
          this.hoveredBlock.active
        ) {
          this.hoveredBlock.setStrokeStyle(
            2,
            0x000000,
            0.4
          );
        }

        if (foundBlock) {
          foundBlock.setStrokeStyle(
            3,
            0xffffff,
            1
          );
        }

        this.hoveredBlock = foundBlock;
      }

      const container = this.$refs.gameContainer;

      if (container) {
        container.style.cursor =
          foundBlock
            ? CURSOR_REMOVE
            : "crosshair";
      }
    },

    drawGrid() {
      if (!this.scene) return;

      const g = this.scene.gridGraphics;
      const u = this.gameState.unit;
      const cols = this.gameState.cols;
      const rows = this.gameState.rows;

      const gridW = cols * u;
      const gridH = rows * u;

      g.clear();

      g.lineStyle(1, 0x334155, 0.4);

      for (let c = 0; c <= cols; c++) {
        g.lineBetween(
          c * u,
          0,
          c * u,
          gridH
        );
      }

      for (let r = 0; r <= rows; r++) {
        g.lineBetween(
          0,
          r * u,
          gridW,
          r * u
        );
      }

      const groundY = this.groundY;

      g.fillStyle(0x1e293b, 1);
      g.fillRect(0, groundY, gridW, u);

      g.lineStyle(2, 0x38bdf8, 1);
      g.lineBetween(0, groundY, gridW, groundY);
    },

    handleKeyboard(e) {
      if (!this.gameState.active || this.gameOver) return;

      if (["1", "2", "3"].includes(e.key)) {
        this.selectBlock(Number(e.key));
      }

      if (
        (e.key === " " || e.key === "Enter") &&
        this.showSplash
      ) {
        this.startGame();
      }
    },

    async startGame() {
      try {
        const data = await startMarketPartida();

        this.idPartida = data.id_partida;
        this.claveTemporal = data.clave_temporal;

        this.verifiedScore = null;
        this.verifiedMoney = null;

        this.gameOver = false;
        this.currentScore = 0;
        this.finalScore = 0;
        this.resDetail = "";
        this.selectedSize = 1;
        this.lastCurveUpdate = 0;

        this.statsInternas =
          this.createInitialStats();

        this.statsInternas.startTime =
          Date.now();

        this.startFading = true;

        setTimeout(() => {
          this.showSplash = false;
          this.gameState.active = true;

          this.startPhase(1);
          this.startTimer();
        }, 500);
      } catch (error) {
        console.error(
          "Error iniciando partida",
          error
        );

        await Swal.fire({
          icon: "info",
          title: "Minijuego no disponible",
          text:
            error.message ||
            "No se pudo iniciar la partida.",
          confirmButtonText: "Entendido",

          customClass: {
            confirmButton:
              "clausulazo-swal-confirm",
          },
        });
      }
    },

    startPhase(n) {
      this.gameState.currentPhase = n;

      const phase = MARKET_PHASES[n];

      this.phaseName = phase.name;

      if (this.breakdownInterval) {
        clearInterval(this.breakdownInterval);
        this.breakdownInterval = null;
      }

      this.triggerCharacterReaction(`phase-${n}`);

      if (phase.type === "fill") {
        this.timeLeft = phase.time;

        if (n === 1) {
          this.generateCurve();
          this.drawCurve();
          this.calculateScore();

          this.flashMsg("MERCADO DIARIO");
        } else {
          this.lastCurveUpdate = 0;

          this.flashMsg("AJUSTE INTRADIARIO");
          this.startBreakdownTimer();
        }
      } else {
        this.gameState.reeTarget = 3;

        this.flashMsg("REE: ELIMINA 3 BLOQUES");
      }
    },

    startBreakdownTimer() {
      if (this.breakdownInterval) {
        clearInterval(this.breakdownInterval);
      }

      this.breakdownInterval = setInterval(() => {
        if (
          !this.gameState.active ||
          this.gameOver ||
          this.gameState.blocks.length === 0
        ) {
          return;
        }

        this.triggerRandomBreakdown();
      }, 8000);
    },

    triggerRandomBreakdown() {
      const state = this.gameState;

      if (state.blocks.length === 0) return;

      const randomIndex = Math.floor(
        Math.random() * state.blocks.length
      );

      const targetBlock =
        state.blocks[randomIndex];

      if (!targetBlock || !targetBlock.active) return;

      this.triggerCharacterReaction(
        "breakdown-warning"
      );

      this.flashMsg(
        "¡AVERÍA EN EL SISTEMA!"
      );

      let flashCount = 0;

      const flashInterval = setInterval(() => {
        if (!targetBlock.active) {
          clearInterval(flashInterval);
          return;
        }

        targetBlock.setVisible(
          !targetBlock.visible
        );

        flashCount++;

        if (flashCount >= 6) {
          clearInterval(flashInterval);

          if (targetBlock.active) {
            if (
              this.hoveredBlock ===
              targetBlock
            ) {
              this.hoveredBlock = null;
            }

            targetBlock.destroy();

            const index =
              state.blocks.indexOf(
                targetBlock
              );

            if (index > -1) {
              state.blocks.splice(index, 1);
            }

            this.statsInternas
              .bloques_eliminados++;

            this.triggerCharacterReaction(
              "block-destroyed"
            );

            this.applyGravity();
            this.calculateScore();
          }
        }
      }, 250);
    },

    updatePhysics() {
      const state = this.gameState;

      if (!state.currentBlock) return;

      state.currentBlock.y += 4;

      let hit = false;

      const groundY = this.groundY;

      if (
        state.currentBlock.y +
          state.currentBlock.height >=
        groundY
      ) {
        state.currentBlock.y =
          groundY -
          state.currentBlock.height;

        hit = true;
      }

      for (const b of state.blocks) {
        if (
          state.currentBlock.x <
            b.x + b.width - 2 &&
          state.currentBlock.x +
            state.currentBlock.width >
            b.x + 2 &&
          state.currentBlock.y +
            state.currentBlock.height >=
            b.y &&
          state.currentBlock.y <
            b.y + b.height
        ) {
          state.currentBlock.y =
            b.y -
            state.currentBlock.height;

          hit = true;
          break;
        }
      }

      if (hit) {
        const u = state.unit;

        state.currentBlock.y =
          Math.round(
            state.currentBlock.y / u
          ) * u;

        state.blocks.push(
          state.currentBlock
        );

        this.statsInternas
          .bloques_colocados++;

        state.currentBlock = null;

        this.triggerCharacterReaction(
          "block-placed"
        );

        this.calculateScore();
      }
    },

    updateCurveFluctuations(time) {
      const state = this.gameState;

      const currentPhaseData =
        MARKET_PHASES[
          state.currentPhase
        ];

      if (
        !currentPhaseData?.name.includes(
          "INTRA"
        )
      ) {
        return;
      }

      const t = time * 0.0015;

      const minRows = 2;

      const maxRows = Math.floor(
        (state.rows - 1) * 0.85
      );

      state.curveColumns =
        state.baseCurveColumns.map(
          (baseRows, c) => {
            const wave1 =
              Math.sin(
                t + c * 0.8
              ) * 1.5;

            const wave2 =
              Math.cos(
                t * 0.7 +
                  c * 0.4
              ) * 0.8;

            return Phaser.Math.Clamp(
              baseRows +
                wave1 +
                wave2,
              minRows,
              maxRows
            );
          }
        );
    },

    drawCurve() {
      if (!this.scene) return;

      const g =
        this.scene.demandGraphics;

      const state =
        this.gameState;

      const u =
        state.unit;

      g.clear();

      if (
        MARKET_PHASES[
          state.currentPhase
        ].type === "ree"
      ) {
        return;
      }

      g.lineStyle(
        3,
        0x38bdf8,
        1
      );

      g.beginPath();

      state.curveColumns.forEach(
        (
          colHeightRows,
          colIndex
        ) => {
          const x1 =
            colIndex * u;

          const x2 =
            (colIndex + 1) * u;

          const y =
            this.groundY -
            colHeightRows * u;

          if (colIndex === 0) {
            g.moveTo(x1, y);
          } else {
            g.lineTo(x1, y);
          }

          g.lineTo(x2, y);
        }
      );

      g.strokePath();
    },

    selectBlock(size) {
      if (!this.canSelectBlock) return;

      if (
        this.selectedSize === size
      ) {
        this.selectedSize = null;

        this.flashMsg(
          "Modo eliminación activado"
        );
      } else {
        this.selectedSize = size;

        this.flashMsg(
          `Bloque ${size}x${size} seleccionado`
        );
      }
    },

    spawnSelectedBlock(pointerX) {
      if (!this.selectedSize) return;

      const size = this.selectedSize;
      const u = this.gameState.unit;
      const s = size * u;
      const W = this.game.config.width;

      const clickCol =
        Math.floor(pointerX / u);

      let targetX =
        clickCol * u -
        Math.floor(size / 2) * u;

      targetX =
        Phaser.Math.Clamp(
          targetX,
          0,
          W - s
        );

      const block =
        this.scene.add
          .rectangle(
            targetX,
            -s,
            s,
            s,
            BLOCK_COLORS[size] ||
              0xffffff
          )
          .setOrigin(0, 0)
          .setStrokeStyle(
            2,
            0x000000,
            0.4
          );

      this.gameState.currentBlock =
        block;
    },

    handleGlobalClick(pointer) {
      if (this.isFalling) return;

      const state = this.gameState;

      const currentPhaseData =
        MARKET_PHASES[
          state.currentPhase
        ];

      if (
        this.selectedSize &&
        !state.currentBlock &&
        currentPhaseData.type !== "ree"
      ) {
        this.spawnSelectedBlock(
          pointer.worldX
        );

        return;
      }

      const isIntradiario =
        currentPhaseData.name.includes(
          "INTRA"
        );

      const isRestricciones =
        currentPhaseData.type === "ree";

      if (
        !isIntradiario &&
        !isRestricciones
      ) {
        return;
      }

      for (
        let i =
          state.blocks.length - 1;
        i >= 0;
        i--
      ) {
        const b = state.blocks[i];

        const rect =
          new Phaser.Geom.Rectangle(
            b.x,
            b.y,
            b.width,
            b.height
          );

        if (
          Phaser.Geom.Rectangle.Contains(
            rect,
            pointer.worldX,
            pointer.worldY
          )
        ) {
          if (this.hoveredBlock === b) {
            this.hoveredBlock = null;
          }

          b.destroy();

          state.blocks.splice(i, 1);

          this.statsInternas
            .bloques_eliminados++;

          this.triggerCharacterReaction(
            "block-destroyed"
          );

          if (
            currentPhaseData.type ===
            "ree"
          ) {
            state.reeTarget--;

            if (
              state.reeTarget <= 0
            ) {
              this.nextPhase();
            }
          }

          this.applyGravity();
          this.calculateScore();

          break;
        }
      }
    },

    applyGravity() {
      const state = this.gameState;

      const groundY =
        this.groundY;

      const sortedBlocks =
        [...state.blocks].sort(
          (a, b) =>
            b.y - a.y
        );

      const targets =
        new Map();

      sortedBlocks.forEach(
        (b) =>
          targets.set(b, b.y)
      );

      let hasMovement = false;

      sortedBlocks.forEach((b) => {
        let lowestY =
          groundY -
          b.height;

        for (const other of sortedBlocks) {
          if (other === b) continue;

          const isOverlapX =
            b.x <
              other.x +
                other.width -
                2 &&
            b.x +
              b.width >
              other.x + 2;

          const otherTargetY =
            targets.get(other);

          const isBelow =
            otherTargetY >=
            b.y +
              b.height -
              2;

          if (
            isOverlapX &&
            isBelow
          ) {
            const possibleY =
              otherTargetY -
              b.height;

            if (
              possibleY <
              lowestY
            ) {
              lowestY =
                possibleY;
            }
          }
        }

        if (
          lowestY >
          b.y + 1
        ) {
          hasMovement = true;

          targets.set(
            b,
            lowestY
          );
        }
      });

      if (hasMovement) {
        this.isFalling = true;

        let animationsRunning = 0;

        sortedBlocks.forEach((b) => {
          const targetY =
            targets.get(b);

          if (targetY !== b.y) {
            animationsRunning++;

            this.scene.tweens.add({
              targets: b,
              y: targetY,
              duration: 300,
              ease: "Bounce.easeOut",

              onComplete: () => {
                animationsRunning--;

                if (
                  animationsRunning ===
                  0
                ) {
                  this.isFalling =
                    false;

                  this.calculateScore();
                }
              },
            });
          }
        });
      } else {
        this.calculateScore();
      }
    },

    generateCurve() {
      const cols =
        this.gameState.cols;

      const playableRows =
        this.gameState.rows -
        1;

      this.gameState
        .baseCurveColumns = [];

      const points = [];

      const numPoints = 4;

      for (
        let i = 0;
        i <= numPoints;
        i++
      ) {
        points.push(
          Math.random() *
            0.6 +
            0.15
        );
      }

      for (
        let c = 0;
        c < cols;
        c++
      ) {
        const progress =
          c / (cols - 1);

        const scaledPoint =
          progress *
          numPoints;

        const index =
          Math.floor(
            scaledPoint
          );

        const fraction =
          scaledPoint -
          index;

        let targetHeightRatio;

        if (
          index >= numPoints
        ) {
          targetHeightRatio =
            points[numPoints];
        } else {
          const p1 =
            points[index];

          const p2 =
            points[index + 1];

          const smoothFraction =
            fraction *
            fraction *
            (3 - 2 * fraction);

          targetHeightRatio =
            p1 +
            (p2 - p1) *
              smoothFraction;
        }

        targetHeightRatio =
          Phaser.Math.Clamp(
            targetHeightRatio,
            0.1,
            0.85
          );

        const colRows =
          Math.round(
            targetHeightRatio *
            playableRows
          );

        this.gameState
          .baseCurveColumns.push(
            colRows
          );
      }

      this.gameState.curveColumns = [
        ...this.gameState
          .baseCurveColumns,
      ];
    },

    calculateScore() {
      const state =
        this.gameState;

      const u =
        state.unit;

      const cols =
        state.cols;

      const playableRows =
        state.rows - 1;

      let target = 0;
      let covered = 0;
      let wasted = 0;

      for (
        let c = 0;
        c < cols;
        c++
      ) {
        const targetRows =
          Math.round(
            state.curveColumns[c] ||
              0
          );

        for (
          let r = 0;
          r < playableRows;
          r++
        ) {
          const x =
            c * u +
            u / 2;

          const y =
            this.groundY -
            r * u -
            u / 2;

          const isTargetCell =
            r < targetRows;

          const hasBlock =
            state.blocks.some(
              (b) =>
                x >= b.x &&
                x <
                  b.x +
                    b.width &&
                y >= b.y &&
                y <
                  b.y +
                    b.height
            );

          if (isTargetCell) {
            target++;

            if (hasBlock) {
              covered++;
            }
          } else if (hasBlock) {
            wasted++;
          }
        }
      }

      this.currentScore =
        Math.round(
          (
            covered /
            (target || 1)
          ) * 100
        ) || 0;

      return {
        perc: this.currentScore,

        wasted: Math.round(
          (
            wasted /
            (target || 1)
          ) * 40
        ),
      };
    },

    startTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }

      this.timerInterval =
        setInterval(() => {
          if (
            !this.gameState.active ||
            MARKET_PHASES[
              this.gameState.currentPhase
            ].type === "ree"
          ) {
            return;
          }

          this.timeLeft--;

          if (this.timeLeft <= 0) {
            this.nextPhase();
          }
        }, 1000);
    },

    nextPhase() {
      if (
        this.gameState.currentPhase <
        2
      ) {
        this.startPhase(
          this.gameState
            .currentPhase + 1
        );
      } else {
        this.endGame();
      }
    },

    async endGame() {
      this.gameState.active = false;

      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }

      if (this.breakdownInterval) {
        clearInterval(
          this.breakdownInterval
        );

        this.breakdownInterval = null;
      }

      const final =
        this.calculateScore();

      this.currentScore =
        final.perc;

      this.finalScore =
        final.perc -
        final.wasted;

      this.resDetail =
        `COBERTURA: ${final.perc}%<br>` +
        `DESVÍO: -${final.wasted}%`;

      this.gameOver = true;

      this.statsInternas.totalTimeMs =
        Date.now() -
        this.statsInternas.startTime;

      this.statsInternas.fases_completadas =
        this.gameState.currentPhase;

      const stats = {
        final_score:
          this.finalScore,

        cobertura:
          final.perc,

        desvio:
          final.wasted,

        fases_completadas:
          this.statsInternas
            .fases_completadas,

        tiempo_jugado_ms:
          this.statsInternas
            .totalTimeMs,

        puntos_locales:
          this.finalScore,
      };

      await this.finalizarPartida(
        stats
      );
    },

    async finalizarPartida(stats) {
      if (!this.claveTemporal) return;

      try {
        const response =
          await finalizeMarketPartida(
            this.idPartida,
            stats,
            this.claveTemporal
          );

        if (
          response.status ===
          "success"
        ) {
          if (!IS_DEV_MODE) {
            this.verifiedScore =
              response.data.score;

            this.verifiedMoney =
              response.data.dinero;

            await Promise.all([
              this.store.refreshStats(),
              this.store.refreshCompany(),
            ]);
          }
        }
      } catch (error) {
        console.error(
          "Error al validar partida",
          error
        );
      }
    },

    flashMsg(text) {
      this.msg = text;

      setTimeout(() => {
        this.msg = "";
      }, 2000);
    },

    resetGame() {
      location.reload();
    },
  },
};
</script>

<style scoped>
.avatar-img {
  width: 100px;
  height: 100px;
  object-fit: contain;
}

.power-grid-wrapper {
  --bg: #000000;
  --panel: #101010;
  --accent: #fc3800;
  --border: #f8f8f8;
  --panel-border: #525252;

  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  background: var(--bg);
  color: #f8f8f8;
  display: flex;
  flex-direction: column;
  font-family: "Courier New", Courier, monospace, "Press Start 2P", system-ui;
  position: relative;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 6px 6px 0 #000000, inset 0 0 0 4px #242424;
  user-select: none;
  image-rendering: pixelated;
}

.dev-banner {
  background: #f83800;
  color: #ffffff;
  text-align: center;
  font-size: 0.7rem;
  font-weight: bold;
  padding: 6px 0;
  letter-spacing: 1px;
  text-transform: uppercase;
  border-bottom: 3px solid #000;
}

#splash-screen {
  position: absolute;
  inset: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
  transition: opacity 0.3s steps(2, start);
  overflow: hidden;
}

.splash-bg-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
  filter: contrast(150%) brightness(70%) saturate(80%);
}

.splash-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 2;
}

.splash-content {
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

#splash-screen.fade-out {
  opacity: 0;
  pointer-events: none;
}

.game-title {
  font-size: 1.5rem;
  font-weight: 900;
  color: #f83800;
  margin-bottom: 16px;
  text-shadow: 3px 3px 0 #000000, -1px -1px 0 #fc9838;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.game-desc {
  font-size: 0.8rem;
  color: #c0c0c0;
  line-height: 1.6;
  text-shadow: 1px 1px 0 #000;
}

#header {
  width: 100%;
  display: grid;
  grid-template-columns: 1.2fr 0.8fr 0.8fr;
  padding: 10px 8px;
  background: var(--panel);
  text-align: center;
  font-weight: 700;
  font-size: 0.75rem;
  letter-spacing: 1px;
  border-bottom: 4px solid #000000;
  box-shadow: inset 0 -2px 0 #525252;
}

.game-body {
  display: flex;
  width: 100%;
  background: #000;
  border-top: 2px solid #242424;
}

#game-container {
  position: relative;
  flex: 1;
  aspect-ratio: 9 / 16;
  max-height: 60vh;
  overflow: hidden;
  cursor: crosshair;
  image-rendering: pixelated;
}

.sidebar-right {
  width: 105px;
  background: #181818;
  border-left: 4px solid #000000;
  box-shadow: inset 2px 0 0 #525252;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px 6px;
  align-items: center;
}

.sidebar-section-blocks {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  align-items: center;
}

.sidebar-title {
  font-size: 0.55rem;
  font-weight: bold;
  color: #fcb800;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 4px;
  text-align: center;
  text-shadow: 1px 1px 0 #000;
}

.character-sprite-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-top: 2px dashed #525252;
  padding-top: 8px;
  margin-top: auto;
}

.sprite-viewport {
  width: 100%;
  aspect-ratio: 1;
  background: #000000;
  border-radius: 0;
  border: 2px solid #525252;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-shadow: inset 2px 2px 0 #242424;
  transition: none;
}

.sprite-viewport.phase-1 {
  border-color: #00f8f8;
  background-color: rgba(0, 248, 248, 0.1);
}

.sprite-viewport.phase-2 {
  border-color: #7878f8;
  background-color: rgba(120, 120, 248, 0.1);
}

.sprite-viewport.block-placed {
  border-color: #00f800;
  background-color: rgba(0, 248, 0, 0.15);
}

.sprite-viewport.block-destroyed {
  border-color: #f80000;
  background-color: rgba(248, 0, 0, 0.15);
}

.sprite-viewport.breakdown-warning {
  border-color: #fcb800;
  background-color: rgba(252, 184, 0, 0.2);
  animation: sprite-shake 0.1s steps(2, start) infinite;
}

@keyframes sprite-shake {
  0%,
  100% {
    transform: translate(0, 0);
  }

  25% {
    transform: translate(-3px, 0);
  }

  75% {
    transform: translate(3px, 0);
  }
}

.sprite-avatar-placeholder {
  font-size: 1.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: sprite-float 0.4s steps(2, start) infinite;
}

@keyframes sprite-float {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-3px);
  }
}

.btn {
  border: none;
  border-radius: 0;
  color: white;
  font-weight: bold;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #323232;
  padding: 8px 4px;
  touch-action: manipulation;
  box-shadow: inset -2px -2px 0 #101010, inset 2px 2px 0 #525252;
}

.btn:active {
  box-shadow: inset 2px 2px 0 #101010;
  transform: translate(1px, 1px);
}

.btn-size {
  width: 100%;
  background: #242424;
  border: 2px solid #525252;
  padding: 10px 4px;
  transition: none;
}

.btn-size span {
  color: #00f8f8;
  font-size: 0.85rem;
  text-shadow: 1px 1px 0 #000;
}

.btn-size small {
  font-size: 0.55rem;
  opacity: 0.9;
}

.btn-size.selected {
  border-color: #fcb800;
  background: #404040;
  box-shadow: inset 2px 2px 0 #101010, 0 0 0 2px #fcb800;
}

.btn-start {
  background: #f83800;
  color: #ffffff;
  padding: 12px 28px;
  border-radius: 0;
  font-weight: bold;
  border: 2px solid #f8f8f8;
  cursor: pointer;
  margin-top: 24px;
  font-size: 0.85rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  box-shadow: 4px 4px 0 #000000, inset -2px -2px 0 #a81000, inset 2px 2px 0 #fc9838;
}

.btn-start:active {
  transform: translate(2px, 2px);
  box-shadow: 2px 2px 0 #000000;
}

.disabled {
  opacity: 0.3;
  cursor: not-allowed;
  box-shadow: none !important;
}

#msg {
  position: absolute;
  top: 8%;
  left: 50%;
  transform: translateX(-50%);
  background: #fcb800;
  color: #000000;
  padding: 6px 16px;
  border-radius: 0;
  font-weight: bold;
  font-size: 0.75rem;
  z-index: 10;
  white-space: nowrap;
  border: 2px solid #000;
  box-shadow: 3px 3px 0 #000000;
  text-transform: uppercase;
  letter-spacing: 1px;
}

#overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 20;
  padding: 16px;
  text-align: center;
}

.verified-txt {
  color: #00f800;
  font-size: 0.75rem;
  margin: 6px 0;
  text-shadow: 1px 1px 0 #000;
}

.dev-txt {
  color: #fcb800;
  font-size: 0.7rem;
  margin-top: 10px;
  text-shadow: 1px 1px 0 #000;
}

.pop-enter-active,
.pop-leave-active {
  transition: none;
}

.pop-enter-from,
.pop-leave-to {
  opacity: 0;
  transform: translate(-50%, -10px);
}

@media (max-width: 768px) {
  .power-grid-wrapper {
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    box-shadow: 3px 3px 0 #000000, inset 0 0 0 3px #242424;
  }

  #header {
    grid-template-columns: 1.2fr 0.7fr 0.9fr;
    padding: 8px 5px;
    font-size: 0.65rem;
    letter-spacing: 0.5px;
  }

  #game-container {
    max-height: none;
  }

  .sidebar-right {
    width: 92px;
    padding: 8px 5px;
  }

  .avatar-img {
    width: 80px;
    height: 80px;
  }
}

@media (max-width: 600px) {
  .power-grid-wrapper {
    width: 100%;
    max-width: 100%;
    border-radius: 0;
    margin: 0;
    box-shadow: 2px 2px 0 #000000, inset 0 0 0 2px #242424;
  }

  #header {
    grid-template-columns: 1.25fr 0.65fr 1fr;
    align-items: center;
    min-height: 42px;
    padding: 6px 4px;
    font-size: 0.58rem;
    line-height: 1.15;
    letter-spacing: 0;
  }

  #phase-txt {
    padding: 0 3px;
  }

  #timer {
    white-space: nowrap;
  }

  #score {
    padding: 0 2px;
    white-space: normal;
  }

  .game-body {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  #game-container {
    width: 100%;
    flex: none;
    aspect-ratio: 3 / 4.8;
    max-height: none;
    min-height: 0;
    cursor: default;
    touch-action: none;
    overscroll-behavior: contain;
  }

  .sidebar-right {
    width: 100%;
    min-height: 92px;
    border-left: none;
    border-top: 4px solid #000000;
    box-shadow: inset 0 2px 0 #525252;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 70px;
    gap: 8px;
    padding: 7px 8px;
  }

  .sidebar-section-blocks {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
    align-items: stretch;
  }

  .sidebar-section-blocks .sidebar-title {
    grid-column: 1 / -1;
    margin: 0 0 2px;
    font-size: 0.52rem;
    line-height: 1;
  }

  .btn-size {
    width: 100%;
    min-width: 0;
    min-height: 50px;
    padding: 5px 2px;
    touch-action: manipulation;
  }

  .btn-size span {
    font-size: 0.78rem;
  }

  .btn-size small {
    margin-top: 2px;
    font-size: 0.5rem;
  }

  .character-sprite-container {
    width: 70px;
    border-top: none;
    border-left: 2px dashed #525252;
    padding: 0 0 0 7px;
    margin: 0;
    justify-content: center;
  }

  .character-sprite-container .sidebar-title {
    display: none;
  }

  .sprite-viewport {
    width: 58px;
    height: 58px;
    aspect-ratio: 1;
  }

  .avatar-img {
    width: 58px;
    height: 58px;
  }

  #msg {
    top: 5%;
    max-width: calc(100% - 20px);
    padding: 5px 10px;
    font-size: 0.6rem;
    letter-spacing: 0.4px;
    white-space: normal;
    text-align: center;
  }

  #splash-screen {
    padding: 14px;
  }

  .splash-content {
    width: 100%;
    max-width: 330px;
  }

  .game-title {
    font-size: 1.15rem;
    line-height: 1.25;
    letter-spacing: 1px;
    margin-bottom: 12px;
  }

  .game-desc {
    font-size: 0.68rem;
    line-height: 1.45;
  }

  .btn-start {
    margin-top: 18px;
    min-width: 145px;
    min-height: 44px;
    padding: 10px 20px;
    font-size: 0.75rem;
    touch-action: manipulation;
  }

  #overlay {
    padding: 12px;
  }

  #overlay h2 {
    font-size: 1.1rem;
    margin-bottom: 10px;
  }

  #res-score {
    font-size: 0.9rem;
  }

  #res-detail {
    font-size: 0.75rem;
    line-height: 1.5;
  }

  .verified-txt {
    font-size: 0.65rem;
  }

  .dev-txt {
    font-size: 0.6rem;
  }

  .btn-reset {
    min-height: 44px;
    margin-top: 8px;
    padding: 8px 16px;
  }

  ::v-deep #game-container canvas {
    touch-action: none !important;
  }
}

@media (max-width: 380px) {
  #header {
    min-height: 40px;
    font-size: 0.52rem;
  }

  #game-container {
    aspect-ratio: 3 / 4.65;
  }

  .sidebar-right {
    grid-template-columns: minmax(0, 1fr) 60px;
    gap: 5px;
    padding: 6px;
  }

  .sidebar-section-blocks {
    gap: 4px;
  }

  .btn-size {
    min-height: 46px;
    padding: 4px 1px;
  }

  .btn-size span {
    font-size: 0.7rem;
  }

  .btn-size small {
    font-size: 0.45rem;
  }

  .character-sprite-container {
    width: 60px;
    padding-left: 5px;
  }

  .sprite-viewport {
    width: 50px;
    height: 50px;
  }

  .avatar-img {
    width: 50px;
    height: 50px;
  }

  .game-title {
    font-size: 1rem;
  }

  .game-desc {
    font-size: 0.62rem;
  }
}
</style>